import React, { useMemo } from "react";
import {
  IonAccordion,
  IonAccordionGroup,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonText,
} from "@ionic/react";
import { WoDocument } from "../models/workorders/Documents";
import { groupDocuments } from "../util/DocumentGroupHelper";
import "./DocumentListDisplay.css";

interface DocumentListDisplayProps {
  documents: WoDocument[];
  onDocumentClick: (doc: WoDocument) => void;
  translations: any;
  translatedMessages: any;
  showLoading: boolean;
  errorData: string;
  isGrouped?: boolean;
}

const DocumentListDisplay: React.FC<DocumentListDisplayProps> = ({
  documents,
  onDocumentClick,
  translations,
  translatedMessages,
  showLoading,
  errorData,
  isGrouped = false,
}) => {
  const groups = useMemo(
    () => (isGrouped ? groupDocuments(documents, translations) : []),
    [isGrouped, documents, translations]
  );

  // Documents the API could not put in a group have nothing to head an
  // accordion with, so they stay in a plain list below the collapsible ones.
  const groupsWithHeading = useMemo(
    () => groups.filter((group) => group.label),
    [groups]
  );
  const groupsWithoutHeading = useMemo(
    () => groups.filter((group) => !group.label),
    [groups]
  );

  // The record's own documents come back first and are what the screen was
  // opened for, so that group starts expanded and the inherited ones start
  // collapsed. "multiple" then lets the user open the others alongside it.
  const expandedGroup = groupsWithHeading[0]?.key;

  const renderDocument = (doc: WoDocument) => {
    let lastModified = doc.LastModified.endsWith("Z")
      ? new Date(doc.LastModified)
      : new Date(doc.LastModified + "Z");
    return (
      <IonItem key={doc.DmsDocId} onClick={() => onDocumentClick(doc)}>
        <IonLabel>
          <h2>{doc.Description}</h2>
          <p>
            {translations["lbl_ememployeedocuments_lastmodifiedby"] ||
              "Last Modified By"}{" "}
            {doc.LastModifiedBy} on {lastModified.toLocaleString()}
          </p>
        </IonLabel>
      </IonItem>
    );
  };

  if (documents && documents.length > 0) {
    if (!isGrouped) {
      return <IonList>{documents.map(renderDocument)}</IonList>;
    }

    return (
      <>
        <IonAccordionGroup multiple value={expandedGroup}>
          {groupsWithHeading.map((group) => (
            <IonAccordion key={group.key} value={group.key}>
              <IonItem slot="header" color="light">
                <IonLabel>
                  {group.label}{" "}
                  <IonNote className="document-group-count">
                    ({group.documents.length})
                  </IonNote>
                </IonLabel>
              </IonItem>
              <IonList slot="content">
                {group.documents.map(renderDocument)}
              </IonList>
            </IonAccordion>
          ))}
        </IonAccordionGroup>
        {groupsWithoutHeading.map((group) => (
          <IonList key={group.key || "ungrouped"}>
            {group.documents.map(renderDocument)}
          </IonList>
        ))}
      </>
    );
  }
  return (
    <IonText
      hidden={showLoading}
      color={errorData ? "danger" : "default"}
      style={{ padding: "16px", display: "inline-block" }}
    >
      {errorData
        ? translatedMessages["ErrDocRetrieving"]?.MessageText ||
          "Error retrieving documents"
        : translatedMessages["NoResultsFound"]?.MessageText ||
          "No results found."}
    </IonText>
  );
};

export default DocumentListDisplay;
